// Lokalizované URL — segmenty cest i slugy přeložené per jazyk (SEO).
// Čeština je základ (bez prefixu); ostatní jazyky mají /{lang}/ prefix (řeší BrowserRouter basename)
// + lokalizovaný segment a slug. Staré (české) slugy zůstávají rozpoznatelné kvůli starým odkazům.
import localizedSlugs from '../data/localizedSlugs.json'
// Segmenty cest + seznam jazyků = jeden zdroj pravdy (sdílí runtime i build skripty:
// scripts/gen-redirects.mjs). Nepřidávat sem duplicitní definice.
import pathSegments from '../data/pathSegments.json'

export type SegKey = 'tools' | 'features' | 'help' | 'blog'

export const ALL_LANGS = pathSegments.langs as readonly string[]
export type Lang = string

/** Lokalizované segmenty cest. cs = základ. Viz src/data/pathSegments.json. */
const SEGMENTS: Record<SegKey, Record<string, string>> = pathSegments.segments

type SlugMap = Record<string, Record<string, string>>
const SLUGS = localizedSlugs as Record<SegKey, SlugMap>

/** Lokalizovaný segment cesty pro daný klíč + jazyk. */
export function seg(key: SegKey, lang: string): string {
  return SEGMENTS[key]?.[lang] || SEGMENTS[key]?.cs || String(key)
}

/** Všechny lokalizované segmenty daného klíče (pro routy / detekci starých URL). */
export function allSegments(key: SegKey): string[] {
  return Array.from(new Set(Object.values(SEGMENTS[key] || {})))
}

// ── Generické lokalizované cesty + reverzní resolve ──────────────────────────

/** Lokalizovaný slug položky daného typu (z českého kanonického slugu). */
export function localizedSlug(type: SegKey, czSlug: string, lang: string): string {
  if (lang === 'cs') return czSlug
  return SLUGS[type]?.[czSlug]?.[lang] || czSlug
}

/** Cesta BEZ jazykového prefixu pro daný typ + český slug + jazyk (Link/canonical/hreflang). */
export function localizedPath(type: SegKey, czSlug: string, lang: string): string {
  return `/${seg(type, lang)}/${localizedSlug(type, czSlug, lang)}`
}

// Reverzní mapy per typ a jazyk { lokalizovanýSlug → czSlug }. Vždy akceptuj i český slug (staré odkazy).
const reverse: Record<string, Record<string, Record<string, string>>> = {}
for (const type of Object.keys(SLUGS) as SegKey[]) {
  reverse[type] = {}
  for (const cz of Object.keys(SLUGS[type] || {})) {
    for (const lang of ALL_LANGS) {
      const loc = lang === 'cs' ? cz : (SLUGS[type][cz]?.[lang] || cz)
      ;(reverse[type][lang] ??= {})[loc] = cz
      ;(reverse[type][lang] ??= {})[cz] = cz // legacy český slug
    }
  }
}

/** Z URL slugu (lokalizovaný NEBO starý český) → kanonický český slug, nebo null. */
export function resolveSlug(type: SegKey, urlSlug: string, lang: string): string | null {
  const direct = reverse[type]?.[lang]?.[urlSlug] ?? reverse[type]?.cs?.[urlSlug]
  if (direct) return direct
  // Cross-lang fallback (robustnost — slug z jiného jazyka, např. po přepnutí jazyka).
  for (const l of ALL_LANGS) {
    const v = reverse[type]?.[l]?.[urlSlug]
    if (v) return v
  }
  return null
}

// Segment → typ (pro přelokalizování celé cesty při přepnutí jazyka).
const SEG_TO_TYPE: Record<string, SegKey> = {}
for (const type of Object.keys(SEGMENTS) as SegKey[]) {
  for (const lang of ALL_LANGS) {
    const s = SEGMENTS[type]?.[lang]
    if (s) SEG_TO_TYPE[s] = type
  }
}

/**
 * Přelokalizuje cestu (BEZ jazykového prefixu) z jednoho jazyka do druhého.
 * /tools/robots-txt-checker (en) → /werkzeuge/robots-txt-prufen (de). Cesty bez
 * lokalizovaného typu (/, /pricing, /tools index…) vrací beze změny.
 */
export function relocalizePath(path: string, fromLang: string, toLang: string): string {
  const parts = path.split('/').filter(Boolean)
  if (parts.length === 0) return path
  const type = SEG_TO_TYPE[parts[0]]
  if (!type) return path
  if (parts.length >= 2) {
    const cz = resolveSlug(type, parts[1], fromLang)
    if (!cz) return path
    const rest = parts.length > 2 ? '/' + parts.slice(2).join('/') : ''
    return localizedPath(type, cz, toLang) + rest
  }
  // Bare segment (index). Jen nápověda má lokalizovaný index segment (/napoveda→/help…);
  // /tools a /blog jsou pevné indexové stránky → ponech.
  if (type === 'help') return `/${seg('help', toLang)}`
  return path
}

// ── Typované zkratky ─────────────────────────────────────────────────────────
export const toolPath = (czSlug: string, lang: string) => localizedPath('tools', czSlug, lang)
export const resolveToolSlug = (urlSlug: string, lang: string) => resolveSlug('tools', urlSlug, lang)
export const featurePath = (czSlug: string, lang: string) => localizedPath('features', czSlug, lang)
export const resolveFeatureSlug = (urlSlug: string, lang: string) => resolveSlug('features', urlSlug, lang)
export const helpPath = (czSlug: string, lang: string) => localizedPath('help', czSlug, lang)
export const resolveHelpSlug = (urlSlug: string, lang: string) => resolveSlug('help', urlSlug, lang)
export const blogPath = (czSlug: string, lang: string) => localizedPath('blog', czSlug, lang)
export const resolveBlogSlug = (urlSlug: string, lang: string) => resolveSlug('blog', urlSlug, lang)
